// Trading Instruments - Indonesian Stocks (Saham IDX)
// Instrument Categories
export type InstrumentCategory = 'saham';

// Indonesian Stock (Saham) Instruments - IDX (PRO tier, sync with OnboardingPage sahamInstruments)
export type SahamInstrument =
  | 'ENRG' | 'ALII' | 'DEWA' | 'BNBR' | 'BUMI' | 'UNSP' | 'VIVA' | 'MDIA'
  | 'BBCA' | 'BBRI' | 'BMRI' | 'TLKM' | 'ASII' | 'BBNI' | 'AMMN' | 'BREN' | 'UNTR' | 'ICBP'
  | 'ANTM' | 'ADRO' | 'MDKA' | 'PGAS' | 'CPIN' | 'INDF' | 'KLBF' | 'UNVR' | 'MYOR'
  | 'GOTO' | 'EMTK' | 'ISAT' | 'CTRA' | 'BBTN' | 'BRIS'
  | 'ARTO' | 'SMGR' | 'TPIA' | 'BRPT' | 'ESSA' | 'INCO' | 'PTBA' | 'ITMG' | 'INKP' | 'TOWR'
  | 'MAPI' | 'EXCL' | 'MEDC' | 'AKRA' | 'PGEO' | 'GGRM' | 'HMSP' | 'INTP' | 'SRTG'
  | 'SMRA' | 'BUKA' | 'SIDO' | 'ERAA' | 'JPFA' | 'ACES' | 'PWON' | 'BSDE' | 'HRUM' | 'TAPG'
  | 'MNCN' | 'AGII' | 'TKIM' | 'PNBN' | 'MBMA' | 'NCKL' | 'BJTM' | 'BJBR' | 'AALI' | 'SCMA'
  | 'CUAN' | 'DSSA' | 'BTPS' | 'PTRO' | 'KINO'
  | 'CNMA' | 'ROTI' | 'SIMP' | 'LPPF' | 'CDIA' | 'INET' | 'ZATA' | 'PADI' | 'WBSA' | 'HUMI'
  | 'EMAS' | 'BULL' | 'BSSR' | 'PANI'
  | 'CBDK' | 'JARR' | 'WIFI' | 'OASA' | 'NASI' | 'HAIS' | 'STRK' | 'SMIL';

// Instrument Type (stock-focused)
export type Instrument = SahamInstrument;

// Stock-specific metrics for AISignal
export interface StockMetrics {
  targetPercentage: number;      // Target profit percentage (e.g., 5-15%)
  stopLossPercentage: number;    // Stop loss percentage (e.g., 3-5%)
  priceIDR: number;              // Price in IDR (no decimals)
  lotSize: number | string;      // Lot size for trading (can be string like "100 lot" or number)
  volume?: string;               // Trading volume description
  foreignFlow?: string;          // Foreign investor flow description
}

// Instrument metadata for display
export interface InstrumentMetadata {
  symbol: string;
  category: InstrumentCategory;
  name: string;
  currency: 'USD' | 'IDR';
  decimals: number;
  exchange?: string;
  yahooSymbol?: string;          // For stocks: SYMBOL.JK
  tradingHours?: {
    open: string;
    close: string;
    timezone: string;
  };
}

// Mapping lama -> baru untuk migrasi data
export const DIRECTION_MIGRATION_MAP: Record<string, 'NAIK' | 'TURUN' | 'NETRAL'> = {
  'BUY': 'NAIK',
  'SELL': 'TURUN',
  'HOLD': 'NETRAL',
  'NAIK': 'NAIK',
  'TURUN': 'TURUN',
  'NETRAL': 'NETRAL',
};

// Signal Direction Types
export type SignalDirection = 'NAIK' | 'TURUN' | 'NETRAL'

// Comprehensive Signal Status Types
export type SignalStatus = 
  | 'PENDING'     // Generated but not yet active
  | 'ACTIVE'      // Currently active and tradeable
  | 'TRIGGERED'   // Entry price hit, position opened
  | 'HIT_TP'      // Take profit reached - WIN
  | 'HIT_SL'      // Stop loss reached - LOSS
  | 'EXPIRED'     // Time-based expiration
  | 'CANCELED'    // Manually canceled
  | 'PARTIAL'     // Partially filled
  | 'MODIFIED'    // Signal parameters updated

// Market Session Types
export type MarketSession = 'IDX_SESSION_1' | 'IDX_SESSION_2' | 'PRE_MARKET' | 'POST_MARKET' | 'CLOSED'

// Risk Profile Types
// Import and re-export RiskProfile from performance types for consistency
import type { RiskProfile } from './performance';
export type { RiskProfile } from './performance';

// Signal Priority Levels
export type SignalPriority = 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL'

// Enhanced Feature Importance with detailed breakdown
export interface FeatureImportance {
  // Technical Analysis Components (0-1)
  technical: number           // Overall technical score
  technicalBreakdown: {
    trendAnalysis: number      // Moving averages, trend lines
    momentum: number           // RSI, MACD, Stochastic
    support_resistance: number // Key levels analysis
    patterns: number           // Chart patterns recognition
    volume: number             // Volume analysis
  }
  
  // Market Sentiment Components (0-1)
  sentiment: number           // Overall sentiment score
  sentimentBreakdown: {
    retail_sentiment: number   // Retail trader positioning
    institutional: number      // Institutional flow data
    options_flow: number       // Options market sentiment
    social_media: number       // Social sentiment analysis
    news_sentiment: number     // News sentiment scoring
  }
  
  // Economic Factors Components (0-1)
  economic: number            // Overall economic score
  economicBreakdown: {
    calendar_events: number    // Upcoming economic releases
    central_bank: number       // Central bank policy impact
    geopolitical: number       // Geopolitical risk assessment
    intermarket: number        // Cross-market relationships
    seasonal: number           // Seasonal patterns
  }
  
  // Additional AI Model Factors
  correlation: number         // Cross-pair correlation analysis
  volatility: number          // Volatility regime analysis
  liquidity: number           // Market liquidity conditions
}

// Enhanced AI Signal with comprehensive trading data
// Note: Many fields are optional to support both simple API responses and full detailed signals
export interface AISignal {
  // Basic Signal Identification (REQUIRED)
  id: string
  instrument: Instrument
  direction: SignalDirection

  // AI Confidence and Analysis (confidence required, details optional)
  confidence: number           // 0-100 or 0.0-1.0 (normalized in UI)
  confidenceLevel?: 'LOW' | 'MEDIUM' | 'HIGH' | 'VERY_HIGH'  // Can be computed from confidence
  aiReasoning: string
  aiReasoningDetailed?: {
    keyFactors: string[]       // Top 3-5 key factors
    marketContext: string      // Current market environment
    riskFactors: string[]      // Potential risks to consider
    timeframe: string          // Primary analysis timeframe
  }

  // Price Action and Risk Management (REQUIRED)
  entryPrice: number
  currentPrice?: number        // Latest market price
  entryRange?: {               // Flexible entry range
    min: number
    max: number
  }
  stopLoss: number
  takeProfit: number
  takeProfitLevels?: {         // Multiple TP levels
    tp1: { price: number, percentage: number }
    tp2: { price: number, percentage: number }
    tp3?: { price: number, percentage: number }
  }

  // Risk Metrics (riskReward required, others optional)
  riskReward: number           // Risk:Reward ratio
  riskProfile?: RiskProfile
  maxRiskPercentage?: number   // Max account risk %
  suggestedPositionSize?: number // % of account

  // Timing and Duration (optional - can be derived from timeframe)
  duration?: number            // Expected duration in hours
  durationRange?: {            // Min/max duration estimates
    min: number
    max: number
  }
  optimalEntry?: {
    session: MarketSession
    timeframe: string          // e.g., "15m", "1h", "4h"
  }

  // Signal Metadata (priority required, others optional)
  priority: SignalPriority
  complexity?: 'SIMPLE' | 'MODERATE' | 'COMPLEX'
  suitability?: {
    beginners: boolean
    intermediate: boolean
    advanced: boolean
  }

  // AI Model Information (all optional - internal metadata)
  modelVersion?: string
  modelType?: 'ENSEMBLE' | 'NEURAL_NETWORK' | 'GRADIENT_BOOSTING' | 'HYBRID'
  featureImportance?: FeatureImportance
  predictionAccuracy?: {
    shortTerm: number          // 1-4 hour accuracy
    mediumTerm: number         // 4-24 hour accuracy
    longTerm: number           // 1-7 day accuracy
  }

  // Status and Lifecycle (generatedAt and expiresAt required)
  status?: SignalStatus        // Defaults to 'ACTIVE' in UI
  generatedAt: Date
  createdAt?: Date | string    // Alternative to generatedAt from API
  activatedAt?: Date           // When signal became active
  expiresAt: Date
  lastUpdatedAt?: Date

  // Market Context (optional - enriched data)
  marketConditions?: {
    volatility: 'LOW' | 'MEDIUM' | 'HIGH' | 'EXTREME'
    trend: 'STRONG_BULLISH' | 'BULLISH' | 'NEUTRAL' | 'BEARISH' | 'STRONG_BEARISH'
    liquidity: 'LOW' | 'NORMAL' | 'HIGH'
    session: MarketSession
    newsImpact: 'NONE' | 'LOW' | 'MEDIUM' | 'HIGH'
  }

  // Performance Tracking (optional)
  performance?: {
    actualResult?: 'WIN' | 'LOSS' | 'BREAKEVEN' | 'PARTIAL_WIN' | 'PARTIAL_LOSS'
    pipsGained?: number
    percentReturn?: number
    maxDrawdown?: number       // Maximum adverse movement
    durationToResult?: number  // Hours to resolution
    executionQuality?: number  // 0-1 how well signal was executed
    usersFollowed?: number
    averageUserReturn?: number
    winRate?: number           // Historical win rate for similar signals
  }

  // Additional Metadata (optional)
  tags?: string[]              // Categorization tags
  relatedSignals?: string[]    // IDs of correlated signals
  backtestResults?: {
    winRate: number
    avgReturn: number
    maxDrawdown: number
    sampleSize: number
  }

  // User Interaction (optional)
  userMetrics?: {
    views: number
    follows: number
    likes: number
    shares: number
    comments: number
  }

  // Stock-specific metrics (for saham instruments only)
  stockMetrics?: StockMetrics

  // Ensemble AI Metadata (optional for backward compatibility)
  ensembleMetadata?: {
    consensusScore: number // 0-100, higher = more agreement between models
    modelVotes: {
      gemini: 'NAIK' | 'TURUN' | 'NETRAL'
      groq: 'NAIK' | 'TURUN' | 'NETRAL'
      openrouter: 'NAIK' | 'TURUN' | 'NETRAL'
      cohere: 'NAIK' | 'TURUN' | 'NETRAL'
    }
    confidenceScores: {
      gemini: number
      groq: number
      openrouter: number
      cohere: number
      weighted: number
    }
    agreementLevel: 'HIGH' | 'MEDIUM' | 'LOW' | 'CONFLICT'
    dominantFactors: {
      technical: number // 0-100
      fundamental: number // 0-100
      sentiment: number // 0-100
      patterns: number // 0-100
    }
    conflictResolution?: string
    marketSession?: string
    adaptiveWeights?: {
      gemini: number
      groq: number
      openrouter: number
      cohere: number
    }
  }

  // Detailed Analysis from Ensemble (optional)
  detailedAnalysis?: {
    technicalAnalysis: string
    fundamentalAnalysis: string
    sentimentAnalysis: string
    patternAnalysis: string
    riskAssessment: string
    riskAssessmentId: string
  }
}

export interface AIModel {
  id: string
  version: string
  type: 'ensemble' | 'xgboost' | 'lightgbm' | 'neural_network'
  accuracy: number
  trainingData: {
    startDate: Date
    endDate: Date
    samples: number
  }
  features: string[]
  hyperparameters: Record<string, unknown>
  deployedAt: Date
  status: 'active' | 'deprecated' | 'testing'
  performance: {
    dailyAccuracy: number
    weeklyAccuracy: number
    monthlyAccuracy: number
  }
}

export interface MarketData {
  instrument: Instrument
  currentPrice: number
  dailyChange: number
  volatility: number
  sentiment: number // -1 to 1
  technicalScore: number // 0-100
  keyLevels: { support: number[]; resistance: number[] }
  lastUpdated: Date
  aiAnalysis: {
    trend: 'bullish' | 'bearish' | 'neutral'
    support: number[]
    resistance: number[]
    recommendation: 'strong_naik' | 'naik' | 'netral' | 'turun' | 'strong_turun'
  }
}


// Enhanced AI Insight with educational and actionable content
export interface AIInsight {
  // Basic Insight Information
  id: string
  type: 'PATTERN' | 'CORRELATION' | 'RISK' | 'OPPORTUNITY' | 'EDUCATION' | 'MARKET_REGIME' | 'ANOMALY'
  category: 'TECHNICAL' | 'FUNDAMENTAL' | 'SENTIMENT' | 'INTERMARKET' | 'BEHAVIORAL' | 'SEASONAL'
  
  // Content
  title: string
  description: string
  detailedAnalysis: string
  
  // Visual aids
  charts?: {
    type: 'PRICE_CHART' | 'INDICATOR_CHART' | 'CORRELATION_MATRIX' | 'HEATMAP'
    data: unknown            // Chart-specific data structure
    title: string
  }[]
  
  // Scope and Relevance
  instrument?: Instrument
  instruments?: Instrument[]  // Multiple instruments if applicable
  timeframe: string          // e.g., "15m", "1h", "4h", "1d"
  relevanceScore: number     // How relevant to current market (0-1)
  
  // AI Confidence and Quality
  confidence: number         // AI confidence in insight (0-1)
  evidenceStrength: 'WEAK' | 'MODERATE' | 'STRONG' | 'VERY_STRONG'
  statisticalSignificance: number  // P-value or similar statistical measure
  
  // Actionability
  actionable: boolean
  actionItems?: {
    action: string
    priority: 'LOW' | 'MEDIUM' | 'HIGH'
    timeframe: string        // When to act
    riskLevel: 'LOW' | 'MEDIUM' | 'HIGH'
  }[]
  
  // Educational Content
  educational: {
    skillLevel: 'BEGINNER' | 'INTERMEDIATE' | 'ADVANCED'
    concepts: string[]       // Related trading concepts
    learningObjectives: string[]
    resources?: {
      type: 'ARTICLE' | 'VIDEO' | 'TUTORIAL' | 'BOOK'
      title: string
      url?: string
    }[]
  }
  
  // Market Context
  marketContext: {
    regime: 'TRENDING' | 'RANGING' | 'VOLATILE' | 'CALM'
    session: MarketSession
    economicEnvironment: 'RISK_ON' | 'RISK_OFF' | 'NEUTRAL'
    applicableConditions: string[]  // When this insight is most relevant
  }
  
  // Performance and Validation
  historical: {
    similarPatterns: number  // Count of similar historical patterns
    successRate: number      // Historical success rate (0-1)
    avgTimeToResolution: number  // Average time for pattern to resolve
    avgPipMovement: number   // Average pip movement when pattern occurs
  }
  
  // Timing and Lifecycle
  createdAt: Date
  validUntil?: Date         // When insight expires
  lastValidated: Date       // Last time insight was validated
  
  // Relationships
  relevantSignals?: string[] // Related signal IDs
  relatedInsights?: string[] // Related insight IDs
  parentInsight?: string     // If this is a follow-up insight
  
  // User Engagement
  userMetrics?: {
    views: number
    likes: number
    bookmarks: number
    shares: number
    helpfulVotes: number
    totalVotes: number
  }
  
  // Source and Attribution
  source: {
    model: string            // AI model that generated insight
    dataSource: string[]     // Primary data sources
    methodology: string      // Brief description of analysis method
  }
  
  // Risk and Disclaimers
  riskWarning?: string      // Specific risk warning if applicable
  disclaimer: string        // Standard disclaimer text
  
  // Tags and Classification
  tags: string[]           // Searchable tags
  complexity: 'SIMPLE' | 'MODERATE' | 'COMPLEX'
  updateFrequency: 'REAL_TIME' | 'HOURLY' | 'DAILY' | 'WEEKLY' | 'ONE_TIME'
}

// Re-export UserSignal from performance types to avoid duplication
export type { UserSignal } from './performance';

// Signal filtering and sorting options
export interface SignalFilter {
  instruments?: Instrument[]
  directions?: SignalDirection[]
  minConfidence?: number
  maxConfidence?: number
  status?: SignalStatus[]
  riskProfile?: RiskProfile[]
  priority?: SignalPriority[]
  timeframe?: string[]       // e.g., ["15m", "1h", "4h"]
  modelTypes?: string[]      // AI model types
  tags?: string[]            // Signal tags
  
  // Performance filters
  minWinRate?: number
  minRiskReward?: number
  maxRiskPercentage?: number
  
  // Date range
  dateRange?: {
    start: Date
    end: Date
  }
}

export interface SignalSort {
  field: 'confidence' | 'generatedAt' | 'riskReward' | 'priority' | 'winRate' | 'performance'
  direction: 'asc' | 'desc'
}

// Re-export SignalPerformance from performance types to avoid duplication
export type { SignalPerformanceMetrics as SignalPerformance } from './performance';

// Trading session and market state information
export interface AITradingSession {
  name: MarketSession
  startTime: string          // HH:MM format in UTC
  endTime: string            // HH:MM format in UTC
  timezone: string
  
  // Session characteristics
  avgVolatility: number      // Average volatility during session
  avgSpread: Partial<Record<Instrument, number>>  // Average spreads
  liquidity: 'LOW' | 'MEDIUM' | 'HIGH'
  
  // Optimal trading conditions
  bestInstruments: Instrument[]  // Most active instruments
  recommendedStrategies: string[]
  
  // Current status
  isActive: boolean
  timeToStart?: number       // Minutes until session starts
  timeToEnd?: number         // Minutes until session ends
}

// Type guards for runtime type checking
export const isAISignal = (obj: unknown): obj is AISignal => {
  return typeof obj === 'object' && obj !== null && 
    'id' in obj && typeof (obj as { id: unknown }).id === 'string' &&
    'instrument' in obj && typeof (obj as { instrument: unknown }).instrument === 'string' &&
    'direction' in obj && ['NAIK', 'TURUN', 'NETRAL'].includes((obj as { direction: unknown }).direction as string) &&
    'confidence' in obj && typeof (obj as { confidence: unknown }).confidence === 'number' &&
    (obj as { confidence: number }).confidence >= 0 && (obj as { confidence: number }).confidence <= 1
}

// All available instruments arrays
export const SAHAM_INSTRUMENTS: SahamInstrument[] = [
  'ENRG', 'ALII', 'DEWA', 'BNBR', 'BUMI', 'UNSP', 'VIVA', 'MDIA',
  'BBCA', 'BBRI', 'BMRI', 'TLKM', 'ASII', 'BBNI', 'AMMN', 'BREN', 'UNTR', 'ICBP',
  'ANTM', 'ADRO', 'MDKA', 'PGAS', 'CPIN', 'INDF', 'KLBF', 'UNVR', 'MYOR',
  'GOTO', 'EMTK', 'ISAT', 'CTRA', 'BBTN', 'BRIS',
  'ARTO', 'SMGR', 'TPIA', 'BRPT', 'ESSA', 'INCO', 'PTBA', 'ITMG', 'INKP', 'TOWR',
  'MAPI', 'EXCL', 'MEDC', 'AKRA', 'PGEO', 'GGRM', 'HMSP', 'INTP', 'SRTG',
  'SMRA', 'BUKA', 'SIDO', 'ERAA', 'JPFA', 'ACES', 'PWON', 'BSDE', 'HRUM', 'TAPG',
  'MNCN', 'AGII', 'TKIM', 'PNBN', 'MBMA', 'NCKL', 'BJTM', 'BJBR', 'AALI', 'SCMA',
  'CUAN', 'DSSA', 'BTPS', 'PTRO', 'KINO',
  'CNMA', 'ROTI', 'SIMP', 'LPPF', 'CDIA', 'INET', 'ZATA', 'PADI', 'WBSA', 'HUMI',
  'EMAS', 'BULL', 'BSSR', 'PANI',
  'CBDK', 'JARR', 'WIFI', 'OASA', 'NASI', 'HAIS', 'STRK', 'SMIL',
];
export const ALL_INSTRUMENTS: Instrument[] = [...SAHAM_INSTRUMENTS];

export const isValidInstrument = (instrument: string): instrument is Instrument => {
  return ALL_INSTRUMENTS.includes(instrument as Instrument)
}

export const isSahamInstrument = (instrument: string): instrument is SahamInstrument => {
  return SAHAM_INSTRUMENTS.includes(instrument as SahamInstrument)
}

export const getInstrumentCategory = (_instrument: Instrument): InstrumentCategory => {
  return 'saham'
}

// Utility functions for working with signals
export const calculatePips = (signal: AISignal): number => {
  // For saham (stocks), return target percentage
  return Math.round(Math.abs((signal.takeProfit - signal.entryPrice) / signal.entryPrice) * 100);
}

export const calculateRiskReward = (entryPrice: number, stopLoss: number, takeProfit: number): number => {
  const risk = Math.abs(entryPrice - stopLoss)
  const reward = Math.abs(takeProfit - entryPrice)
  return risk > 0 ? Number((reward / risk).toFixed(2)) : 0
}

export const formatPrice = (price: number, _instrument: Instrument): string => {
  // Format as IDR without decimals for stock instruments
  return `Rp ${Math.round(price).toLocaleString('id-ID')}`;
}

// Format percentage for stock signals
export const formatPercentage = (value: number): string => {
  return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
}

// Constants for signal generation and validation
// Ensemble Signals Response Interface
export interface EnsembleSignalsResponse {
  success: boolean
  data: {
    signals: AISignal[]
    overallMarketBias: 'BULLISH' | 'BEARISH' | 'NEUTRAL' | 'MIXED'
    consensusMetrics: {
      averageAgreement: number
      highConfidenceSignals: number
      conflictingSignals: number
      modelReliability: {
        gemini: number
        groq: number
        openrouter: number
        cohere: number
      }
    }
    marketInsights: {
      dominantTheme: string
      riskLevel: 'LOW' | 'MEDIUM' | 'HIGH'
      opportunityScore: number
      warningFlags: string[]
    }
    tierOptimization: {
      currentTier: string
      signalsGenerated: number
      upgradeRecommendation?: string
    }
    indonesianContext: {
      wibTradingRecommendation: string
      sessionOptimization: string
      localMarketFactors: string[]
    }
    performance: {
      processingTime: number
      apiCallsUsed: number
      cacheHitRate: number
    }
    generatedAt: string
    aiProvider: string
    totalSignals: number
    averageConfidence: number
  }
  cached?: boolean
  modelHealth?: {
    overall: 'HEALTHY' | 'DEGRADED' | 'CRITICAL'
    models: Record<string, 'ONLINE' | 'OFFLINE' | 'LIMITED'>
    lastChecked: string
  }
}

// Model Votes Display Interface
export interface ModelVote {
  model: 'gemini' | 'groq' | 'openrouter' | 'cohere'
  vote: 'NAIK' | 'TURUN' | 'NETRAL'
  confidence: number
  reasoning?: string
}

// Consensus Quality Levels
export type ConsensusQuality = 'PERFECT' | 'HIGH' | 'MEDIUM' | 'LOW' | 'CONFLICT'

export const SIGNAL_CONSTANTS = {
  // Confidence ranges
  MIN_CONFIDENCE: 0.5,
  MAX_CONFIDENCE: 0.95,
  HIGH_CONFIDENCE_THRESHOLD: 0.8,
  MEDIUM_CONFIDENCE_THRESHOLD: 0.65,
  
  // Risk management
  MAX_RISK_REWARD: 10,
  MIN_RISK_REWARD: 0.5,
  DEFAULT_RISK_REWARD: 2,
  
  // Duration limits (hours)
  MIN_DURATION: 1,
  MAX_DURATION: 168, // 1 week
  DEFAULT_DURATION: 24,
  
  // Percentage values for stock instruments (0.01 = 1%)
  PIP_VALUES: {
    ENRG: 0.01, ALII: 0.01, DEWA: 0.01, BNBR: 0.01, BUMI: 0.01, UNSP: 0.01, VIVA: 0.01, MDIA: 0.01,
    BBCA: 0.01, BBRI: 0.01, BMRI: 0.01, TLKM: 0.01, ASII: 0.01, BBNI: 0.01, AMMN: 0.01, BREN: 0.01, UNTR: 0.01, ICBP: 0.01,
    ANTM: 0.01, ADRO: 0.01, MDKA: 0.01, PGAS: 0.01, CPIN: 0.01, INDF: 0.01, KLBF: 0.01, UNVR: 0.01, MYOR: 0.01,
    GOTO: 0.01, EMTK: 0.01, ISAT: 0.01, CTRA: 0.01, BBTN: 0.01, BRIS: 0.01,
  } as Record<Instrument, number>,

  // Saham-specific constants
  SAHAM_MIN_STOP_LOSS_PERCENT: 3,
  SAHAM_MAX_STOP_LOSS_PERCENT: 5,
  SAHAM_MIN_TARGET_PERCENT: 5,
  SAHAM_MAX_TARGET_PERCENT: 15,
  SAHAM_MIN_RISK_REWARD: 2
} as const